'use client';

import { createContext, useContext, useEffect, useRef } from 'react';

import { useCapacitor } from './use-capacitor';
import { CAPACITOR_COOKIE } from './constants';

interface CapacitorContextValue {
  isNative: boolean;
  isReady: boolean;
}

const CapacitorContext = createContext<CapacitorContextValue>({
  isNative: false,
  isReady: false,
});

/**
 * Provides Capacitor native detection to the component tree.
 * Hides the native splash screen once the app has mounted,
 * ensuring content is visible before the splash disappears.
 *
 * Also sets a persistent cookie so the server can detect Capacitor
 * on subsequent requests and skip rendering the web layout.
 */
export function CapacitorProvider({ children }: { children: React.ReactNode }) {
  const state = useCapacitor();
  const splashHidden = useRef(false);

  useEffect(() => {
    if (state.isNative && !splashHidden.current) {
      splashHidden.current = true;

      // Set a persistent cookie so the server renders native layout on future requests
      document.cookie = `${CAPACITOR_COOKIE}=1;path=/;max-age=31536000;SameSite=Lax`;

      // Call the SplashScreen plugin via Capacitor's bridge directly,
      // avoiding a build-time dependency on @capacitor/splash-screen in the web app.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cap = (window as any).Capacitor;

      cap?.Plugins?.SplashScreen?.hide({ fadeOutDuration: 300 });
    }
  }, [state.isNative]);

  return (
    <CapacitorContext.Provider value={state}>
      {children}
    </CapacitorContext.Provider>
  );
}

/**
 * Returns whether the app is running inside a Capacitor native shell.
 * Must be used within a CapacitorProvider.
 */
export function useIsNative() {
  return useContext(CapacitorContext);
}
